import React, { useState, useMemo } from 'react';
import { DateTextInput } from './DateTextInput.jsx';
import { NumericField } from './NumberInput.jsx';
import { fmtMoney } from '../engine.js';
import { CHART_OF_ACCOUNTS } from '../data.js';
import { todayISO } from '../formatting.js';

const PM_BTN = 'bg-transparent border border-border-strong text-text-muted px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:text-text hover:border-accent-gold disabled:opacity-50 disabled:cursor-not-allowed';
const PM_BTN_PRIMARY = 'bg-[rgba(240,180,41,0.10)] border border-accent-gold text-accent-gold px-[0.7rem] py-[0.4rem] rounded-sm cursor-pointer font-[inherit] text-[0.78rem] hover:bg-[rgba(240,180,41,0.18)] disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_ICON = 'bg-surface-raised text-text-muted border border-border rounded-sm w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:border-red-soft';
const BTN_GHOST_SM = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-2 py-[2px] rounded-sm font-[inherit] text-[11px] cursor-pointer hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const FORM_INPUT = 'bg-surface-raised border border-border-strong text-text px-[6px] py-[3px] rounded-sm font-[inherit] text-[0.85rem] w-full focus:outline-none focus:border-accent-blue';
const LABEL_WRAP = 'flex flex-col gap-[0.3rem] py-[0.45rem]';
const LABEL_TEXT = 'text-[0.82rem] text-text flex justify-between items-baseline gap-[0.4rem]';
const INPUT_WRAP = 'flex items-center bg-surface-raised border border-border rounded-sm overflow-hidden focus-within:border-accent-blue';
const INNER_INPUT = 'bg-transparent text-text border-0 px-[0.55rem] py-[0.4rem] w-full font-[inherit] text-[0.85rem] tabular-nums focus:outline-none';

export default function JournalEntryForm({ onPost, onSaveDraft, onCancel, initial }) {
  const [date, setDate]   = useState(initial?.date ?? todayISO());
  const [memo, setMemo]   = useState(initial?.memo ?? '');
  const [lines, setLines] = useState(initial?.lines ?? [
    { account: '', debit: 0, credit: 0 },
    { account: '', debit: 0, credit: 0 },
  ]);

  const { totalDr, totalCr, diff, balanced } = useMemo(() => {
    const dr = lines.reduce((s, l) => s + (Number(l.debit)  || 0), 0);
    const cr = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
    const d  = dr - cr;
    return { totalDr: dr, totalCr: cr, diff: d, balanced: Math.abs(d) < 0.005 };
  }, [lines]);

  const setLine = (i, patch) => {
    setLines(arr => arr.map((l, j) => j === i ? { ...l, ...patch } : l));
  };
  const addLine = () => setLines(arr => [...arr, { account: '', debit: 0, credit: 0 }]);
  const removeLine = (i) => setLines(arr => arr.filter((_, j) => j !== i));

  const buildEntry = (status) => ({
    id: `adj-${Date.now().toString(36)}`,
    date,
    memo: memo.trim(),
    lines: lines.filter(l => l.account && (Number(l.debit) > 0 || Number(l.credit) > 0)),
    status,
    affects: inferAffects(lines),
    postedAt: status === 'posted' ? new Date().toISOString() : null,
  });

  const canPost = balanced && lines.some(l => l.account && (Number(l.debit) > 0 || Number(l.credit) > 0));

  const reset = () => {
    setMemo('');
    setLines([
      { account: '', debit: 0, credit: 0 },
      { account: '', debit: 0, credit: 0 },
    ]);
  };

  const cellCls = 'text-left px-[0.4rem] py-[0.3rem] border-b border-dashed border-border';
  const headCls = `${cellCls} text-text-dim font-medium`;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-3 flex-wrap items-end">
        <label className={LABEL_WRAP}>
          <span className={LABEL_TEXT}>Date</span>
          <span className={INPUT_WRAP}>
            <DateTextInput className={INNER_INPUT} value={date} onChange={(e) => setDate(e.target.value)} />
          </span>
        </label>
        <label className={`${LABEL_WRAP} flex-1 min-w-[260px]`}>
          <span className={LABEL_TEXT}>Memo</span>
          <span className={INPUT_WRAP}>
            <input
              type="text"
              className={`${INNER_INPUT} w-full text-left`}
              placeholder="e.g. Accrue utilities not yet billed"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </span>
        </label>
      </div>

      <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[0.85rem]">
        <thead>
          <tr>
            <th className={headCls} style={{ width: '50%' }}>Account</th>
            <th className={headCls} style={{ width: '18%' }}>Debit</th>
            <th className={headCls} style={{ width: '18%' }}>Credit</th>
            <th className={headCls}></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className={cellCls}>
                <input
                  type="text"
                  className={FORM_INPUT}
                  list="je-accounts"
                  placeholder="Account name…"
                  value={l.account}
                  onChange={(e) => setLine(i, { account: e.target.value })}
                />
              </td>
              <td className={cellCls}>
                <NumericField
                  className={`${FORM_INPUT} text-right tabular-nums`}
                  value={l.debit || 0}
                  onChange={(v) => setLine(i, { debit: v, credit: 0 })}
                  decimals={2}
                  min={0}
                />
              </td>
              <td className={cellCls}>
                <NumericField
                  className={`${FORM_INPUT} text-right tabular-nums`}
                  value={l.credit || 0}
                  onChange={(v) => setLine(i, { credit: v, debit: 0 })}
                  decimals={2}
                  min={0}
                />
              </td>
              <td className={cellCls}>
                <button type="button" className={BTN_ICON} onClick={() => removeLine(i)} aria-label="Remove line">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className={cellCls}>
              <button type="button" className={BTN_GHOST_SM} onClick={addLine}>+ Add line</button>
            </td>
            <td className={`${cellCls} text-right tabular-nums`}><strong>{fmtMoney(totalDr, { decimals: 2 })}</strong></td>
            <td className={`${cellCls} text-right tabular-nums`}><strong>{fmtMoney(totalCr, { decimals: 2 })}</strong></td>
            <td className={cellCls}>
              {balanced
                ? <span className="text-[0.78rem] px-2 py-[3px] rounded-sm bg-[rgba(74,222,128,0.15)] text-accent-green border border-[rgba(74,222,128,0.4)]">Balanced ✓</span>
                : <span className="text-[0.78rem] px-2 py-[3px] rounded-sm bg-[rgba(248,113,113,0.14)] text-accent-red border border-[rgba(248,113,113,0.4)]">Unbalanced: {fmtMoney(diff, { decimals: 2 })}</span>}
            </td>
          </tr>
        </tfoot>
      </table>
      </div>

      {/* datalist — all CHART_OF_ACCOUNTS rows as suggestions */}
      <datalist id="je-accounts">
        {CHART_OF_ACCOUNTS.map(a => <option key={a.id} value={a.label} />)}
      </datalist>

      <div className="flex gap-[0.4rem] justify-end flex-wrap">
        <button type="button" className={PM_BTN} onClick={() => { onCancel?.(); reset(); }}>Cancel</button>
        <button type="button" className={PM_BTN} onClick={() => { onSaveDraft?.(buildEntry('draft')); reset(); }}>Save as Draft</button>
        <button
          type="button"
          className={PM_BTN_PRIMARY}
          disabled={!canPost}
          onClick={() => { onPost?.(buildEntry('posted')); reset(); }}
          title={canPost ? 'Post this balanced entry' : 'Entry must be balanced and have at least one line'}
        >
          Post entry
        </button>
      </div>
    </div>
  );
}

/* Infer which statements this entry affects from line accounts. */
function inferAffects(lines) {
  const affects = new Set();
  for (const l of lines) {
    if (!l.account) continue;
    const a = l.account.toLowerCase();
    if (a.includes('revenue') || a.includes('expense') || a.includes('income')) affects.add('is');
    if (a.includes('asset') || a.includes('payable') || a.includes('receivable') || a.includes('cash') || a.includes('bank')) affects.add('bs');
    if (a.includes('equity') || a.includes('retained')) affects.add('bs');
  }
  return Array.from(affects);
}
